import { getLocales, getExtendedLocales } from "configOptions/locales.mjs";
// import { getTranslationFiles } from "./utils";
import { pluralSuffixes as pluralSuffixesByLocale } from "./plurals";
import environment from "@/modules/environment";

type TKey = StringKey | PluralKey;
type TOptions = { plural?: boolean; values?: { [key: string]: any }; namespace?: "common" };

type Strings = { [key in StringKey]: string } | null;
type Plurals = { [key in PluralKey]: string } | null;
type CommonStrings = { [key in CommonStringKey]: string } | null;

type InitParams = {
  locale: LanguageCode;
  stringFile: Strings;
  pluralFile: Plurals;
  commonStringFile: CommonStrings;
};

export class I18n {
  static defaultLocale = "en" as const;

  private static _locale: LanguageCode = "en";
  static get locale(): LanguageCode {
    if (environment.isServer && !this.isLoaded)
      console.warn(
        "'I18n.locale' is being accessed before initialization, should happend only on first load in dev mode"
      );
    return this._locale;
  }
  static set locale(value: LanguageCode) {
    this._locale = value;
  }

  static locales = getLocales(environment.appName);
  static extendedLocales = getExtendedLocales(environment.appName);
  static strings: Strings;
  static plurals: Plurals;
  static isLoaded = false;

  private static stringsByNamespace: { ["common"]?: CommonStrings } = {};

  private static loadFromDOM = () => {
    const dataString = document.querySelector("#__PAGE_DATA__")?.innerHTML;
    const data = JSON.parse(dataString || "{}");

    this._locale = document.documentElement.lang as LanguageCode;
    this.strings = data.strings;
    this.plurals = data.plurals;
    this.stringsByNamespace["common"] = data.commonStrings;
  };

  // private static loadFiles = async (locale: LanguageCode) => {
  //   const { stringFile, pluralFile, commonStringFile } = await getTranslationFiles(locale);
  //   this._locale = locale;
  //   this.strings = stringFile;
  //   this.plurals = pluralFile;
  //   this.stringsByNamespace["common"] = commonStringFile;
  // };

  static init = (params?: InitParams) => {
    if (environment.isServer && params) {
      this._locale = params.locale;
      this.strings = params.stringFile;
      this.plurals = params.pluralFile;
      this.stringsByNamespace["common"] = params.commonStringFile;
    } else this.loadFromDOM();

    this.isLoaded = true;
  };

  static tWithNamespace = (key: CommonStringKey, namespace = "common" as const) => {
    if (!this.isLoaded && !environment.isServer) this.init();
    if (!this.stringsByNamespace[namespace]) return key;
    const strings = this.stringsByNamespace[namespace];
    const string = strings[key as keyof CommonStrings];
    return string || key;
  };

  static translate = (key: TKey, options?: TOptions) => {
    if (!this.isLoaded && !environment.isServer) this.init();
    if (!this.strings || !this.plurals) return key;
    let string = "";

    if (options?.plural && options.values) {
      // counts only with the first value used in the plural
      const pluralSuffix = (pluralSuffixesByLocale[this._locale] || pluralSuffixesByLocale[this.defaultLocale])(
        Object.values(options.values)[0]
      );
      const pluralKey = `${key as PluralKey}_${pluralSuffix}` as keyof Plurals;
      string = this.plurals[pluralKey];
    } else {
      string = this.strings[key as keyof Strings];
    }

    if (options?.values && string) {
      Object.entries(options.values).forEach(([key, value]) => {
        string = string.replace(`{{${key}}}`, value);
      });
    }

    return string || key;
  };
  static t: typeof this.translate = (...args) => this.translate(...args);
}
